<template>
  <v-container fluid>
    <h2>{{ $tt('DocFormTabNameTaskRecordList') }}</h2>
    <p class="ml-2 mb-2 caption grey--text">{{ $tt('feed.derivedRecordsDescription') }}</p>

    <v-row class="ml-0 mb-0" align="center">
      <v-col cols="6" sm="3">
        <v-text-field v-model="dateFrom" type="date" dense hide-details :label="$tt('customDateFilterStartDate')" />
      </v-col>
      <v-col cols="6" sm="3">
        <v-text-field v-model="dateTo" type="date" dense hide-details :label="$tt('customDateFilterEndDate')" />
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-btn small color="primary" outlined @click="manualDialog = true">
          <v-icon small left>mdi-pencil-plus</v-icon>
          {{ $tt('feed.addManualRecord') }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- 走行行の選択 → マージ / 空間編集 (クリップ・圃場化) -->
    <v-layout v-if="selectedIds.length" align-center class="ml-2 mt-1">
      <span class="caption mr-3">{{ $tt('feed.selected') }}: {{ selectedIds.length }}</span>
      <v-btn small color="primary" class="mr-2" @click="mergeDialog = { show: true, name: '', memo: '' }">
        {{ $tt('feed.mergeSave') }}
      </v-btn>
      <v-btn small color="primary" outlined @click="goSpatialEdit()">
        {{ $tt('feed.spatialEdit') }}
      </v-btn>
    </v-layout>

    <v-simple-table v-if="rows.length > 0" class="mt-2">
      <tbody>
        <tr v-for="row in rows" :key="row.key" class="clickable-row" @click="onRowClick(row)">
          <td style="width: 40px" @click.stop>
            <v-checkbox
              v-if="row.virtual"
              dense
              hide-details
              class="mt-0 pt-0"
              :input-value="selectedIds.includes(row.id)"
              @change="toggleSelect(row.id)"
            />
          </td>
          <td style="min-width: 110px">
            <div class="caption grey--text">{{ row.dateText }}</div>
            <v-chip v-if="row.virtual" x-small outlined>{{ $tt('feed.trajectoryChip') }}</v-chip>
            <v-chip v-else-if="row.manual" x-small outlined>{{ $tt('feed.manualChip') }}</v-chip>
            <v-chip v-else x-small outlined color="purple">{{ $tt('feed.derivedChip') }}</v-chip>
          </td>
          <td>
            <operation-type-indicator-component :operationType="row.rawOp" :displayBorder="true" />
          </td>
          <td>
            <strong class="d-inline-block text-truncate" style="max-width: 180px">{{ row.name || '-----' }}</strong>
            <div v-if="row.memo" class="caption grey--text text-truncate" style="max-width: 180px">{{ row.memo }}</div>
          </td>
          <td>
            <span v-if="row.distanceKm" class="grey--text caption">{{ row.distanceKm }} km</span>
            <span v-else-if="row.manual && row.record.photos && row.record.photos.length" class="grey--text caption">
              <v-icon small>mdi-camera</v-icon> {{ row.record.photos.length }}
            </span>
          </td>
          <td class="text-right" @click.stop style="min-width: 150px">
            <v-btn v-if="!row.manual" small text color="primary" @click="showOnMap(row)">
              <v-icon small>mdi-map-search-outline</v-icon>
            </v-btn>
            <v-btn v-if="row.virtual" small text color="primary" :to="'/tasks/' + row.id">
              <v-icon small>mdi-content-cut</v-icon>
            </v-btn>
            <v-btn v-if="row.manual" small text color="primary" @click="shareManual(row.record)">
              <v-icon small>mdi-share-variant</v-icon>
            </v-btn>
            <v-btn v-if="row.manual" small text color="error" @click="manualDeleteTarget = row.record">
              <v-icon small>mdi-trash-can-outline</v-icon>
            </v-btn>
            <v-btn v-if="!row.virtual && !row.manual" small text color="error" @click="deleteTarget = row">
              <v-icon small>mdi-trash-can-outline</v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    <p v-else class="ml-2 mt-4 grey--text">{{ $tt('feed.noManualRecords') }}</p>

    <!-- 手動記録の詳細 -->
    <v-dialog :value="!!manualDetail" max-width="480" @input="manualDetail = null">
      <v-card v-if="manualDetail">
        <v-card-title class="subtitle-1">
          <v-chip x-small outlined class="mr-2">{{ $tt('feed.manualChip') }}</v-chip>
          {{ getLocalizedOperationType(manualDetail.operation_type) }}
        </v-card-title>
        <v-card-text>
          <v-simple-table dense>
            <tbody>
              <tr>
                <td class="grey--text">{{ $tt('feed.recordStart') }}</td>
                <td>{{ formatManualDt(manualDetail.started_at) }}</td>
              </tr>
              <tr v-if="manualDetail.ended_at">
                <td class="grey--text">{{ $tt('feed.recordEnd') }}</td>
                <td>{{ formatManualDt(manualDetail.ended_at) }}</td>
              </tr>
              <tr v-if="manualDurationMin(manualDetail)">
                <td class="grey--text">{{ $tt('feed.statTime') }}</td>
                <td>{{ manualDurationMin(manualDetail) }} min</td>
              </tr>
              <tr v-if="manualDetail.field_name">
                <td class="grey--text">{{ $tt('customFieldHeader') }}</td>
                <td>{{ manualDetail.field_name }}</td>
              </tr>
              <tr v-if="manualDetail.memo">
                <td class="grey--text">{{ $tt('feed.recordMemo') }}</td>
                <td>{{ manualDetail.memo }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <div v-if="manualDetail.photos && manualDetail.photos.length" class="manual-detail-photos mt-3">
            <img v-for="p in manualDetail.photos" :key="p.id" :src="p.thumb_url" class="manual-detail-thumb" />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="manualDetail = null">{{ $tt('customClose') }}</v-btn>
          <v-btn
            color="primary"
            text
            @click="
              shareManual(manualDetail)
              manualDetail = null
            "
          >
            {{ $tt('feed.share') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- マージ保存 -->
    <v-dialog v-model="mergeDialog.show" max-width="360">
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.mergeSave') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="mergeDialog.name" dense :label="$tt('feed.workName')" />
          <v-textarea v-model="mergeDialog.memo" dense rows="2" :label="$tt('feed.recordMemo')" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="mergeDialog.show = false">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!mergeDialog.name" @click="saveMerge()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 手動記録の追加/削除/共有 -->
    <manual-work-dialog v-model="manualDialog" @saved="loadManual()" />
    <v-dialog :value="!!manualDeleteTarget" max-width="360" @input="manualDeleteTarget = null">
      <v-card>
        <v-card-text class="pt-4">{{ $tt('feed.recordDeleteConfirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="manualDeleteTarget = null">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="error" text @click="deleteManual()">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <share-post-dialog v-model="shareDialog" :taskMeta="shareMeta" :initialPhotoIds="sharePhotoIds" />

    <!-- 派生記録の削除確認 -->
    <v-dialog :value="!!deleteTarget" max-width="360" @input="deleteTarget = null">
      <v-card>
        <v-card-text class="pt-4">{{ $tt('feed.derivedDeleteConfirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteTarget = null">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="error" text @click="deleteWork()">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import SharePostDialog from '@/components/feed/SharePostDialog'
import OperationTypeMixin from '@/components/mixins/operation-type-mixin'
import OperationTypeIndicatorComponent from '@/components/operation/OperationTypeIndicatorComponent'
import ManualWorkDialog from '@/components/records/ManualWorkDialog'
import connectDatastoreClient from '@/lib/connect-client/connect-datastore-client'
import connectNaviClient from '@/lib/connect-client/connect-navi-client'
import feedApiClient from '@/lib/feed-api-client'
import { aidTaskRecordDistance } from '@/lib/filters'
import Toast from '@/lib/toast'
import UserInfo from '@/lib/user-info'

export default {
  name: 'WorkRecords',
  mixins: [OperationTypeMixin],
  components: { OperationTypeIndicatorComponent, ManualWorkDialog, SharePostDialog },
  data() {
    return {
      dateFrom: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
      dateTo: new Date().toISOString().slice(0, 10),
      tasks: [],
      works: [],
      manualRecords: [],
      deleteTarget: null,
      manualDialog: false,
      manualDetail: null,
      manualDeleteTarget: null,
      shareDialog: false,
      shareMeta: null,
      sharePhotoIds: [],
      selectedIds: [],
      mergeDialog: { show: false, name: '', memo: '' },
      saving: false,
      loadSeq: 0,
    }
  },
  computed: {
    // 走行・移動軌跡と一対の作業記録 (自動) + 編集で作られた派生記録 + 手動記録を時系列で混在表示
    rows() {
      const out = []
      for (const t of this.tasks) {
        const distM = aidTaskRecordDistance(t)
        out.push({
          key: 't-' + t.id,
          virtual: true,
          id: t.id,
          rawOp: t.operationType,
          name: t.name || this.getLocalizedOperationType(t.operationType),
          memo: '',
          dateText: this.fmt(t.startTime),
          time: Date.parse(t.startTime) || 0,
          startTime: t.startTime || null,
          distM: typeof distM === 'number' && isFinite(distM) ? distM : null,
          distanceKm: typeof distM === 'number' && isFinite(distM) ? (distM / 1000).toFixed(2) : null,
        })
      }
      for (const w of this.works) {
        out.push({
          key: 'w-' + w.id,
          virtual: false,
          id: w.id,
          rawOp: w.operation_type,
          name: w.name,
          memo: w.memo,
          sources: w.sources,
          dateText: this.fmt(w.started_at),
          time: Date.parse(w.started_at) || 0,
          distanceKm: w.cache && w.cache.distance_m ? (w.cache.distance_m / 1000).toFixed(2) : null,
        })
      }
      for (const m of this.manualRecords) {
        if ((m.kind || 'work') !== 'work') continue
        out.push({
          key: 'm-' + m.id,
          manual: true,
          record: m,
          id: m.id,
          rawOp: m.operation_type,
          name: m.field_name || this.getLocalizedOperationType(m.operation_type),
          memo: m.memo,
          dateText: this.formatManualDt(m.started_at),
          time: Date.parse(m.started_at) || 0,
          distanceKm: null,
        })
      }
      out.sort((a, b) => b.time - a.time)
      return out
    },
  },
  watch: {
    dateFrom() {
      this.loadTasks()
    },
    dateTo() {
      this.loadTasks()
    },
  },
  mounted() {
    this.loadTasks()
    this.loadWorks()
    this.loadManual()
  },
  methods: {
    fmt(s) {
      if (!s) return ''
      const d = new Date(s)
      if (isNaN(d)) return ''
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatManualDt(s) {
      if (!s) return ''
      return s.replace('T', ' ').slice(0, 16)
    },
    async loadTasks() {
      const seq = ++this.loadSeq
      this.tasks = []
      const username = this.$store.getters.loginUsername
      if (!username || !this.dateFrom || !this.dateTo) return
      const filter = {
        dateTimeRange: {
          type: 'Start',
          from: new Date(this.dateFrom + 'T00:00:00').toISOString(),
          to: new Date(this.dateTo + 'T23:59:59').toISOString(),
        },
        queries: [{ type: 'Account', uuids: [username] }],
      }
      try {
        const client = UserInfo.isFSMigrated() ? connectDatastoreClient : connectNaviClient
        const res = await client.getTasksWithFilter(filter, { page: 0, pageSize: 50 })
        if (seq === this.loadSeq) {
          this.tasks = (res.data && res.data.contents) || []
        }
      } catch (e) {
        console.error(e.response || e)
      }
    },
    async loadWorks() {
      try {
        const res = await feedApiClient.workRecords()
        this.works = res.data.records || []
      } catch (e) {
        console.error(e.response || e)
      }
    },
    async loadManual() {
      try {
        const res = await feedApiClient.manualRecords()
        this.manualRecords = res.data.records || []
      } catch (e) {
        console.error(e.response || e)
      }
    },
    toggleSelect(id) {
      const i = this.selectedIds.indexOf(id)
      if (i >= 0) {
        this.selectedIds.splice(i, 1)
      } else {
        this.selectedIds.push(id)
      }
    },
    onRowClick(row) {
      if (row.virtual) {
        // 走行軌跡との一対レコード: 走行記録詳細で区間編集できる
        this.$router.push('/tasks/' + row.id)
      } else if (row.manual) {
        this.manualDetail = row.record
      } else {
        // 派生記録の詳細は走行・移動軌跡と同じく専用ページで表示
        this.$router.push('/works/' + row.id)
      }
    },
    goSpatialEdit() {
      // 先頭 (最古) の走行の作業タイプ・開始日時を編集画面に引き継ぐ
      const rows = this.rows.filter(r => r.virtual && this.selectedIds.includes(r.id)).sort((a, b) => a.time - b.time)
      const first = rows[0] || {}
      this.$router.push({
        path: '/works/edit',
        query: { tasks: this.selectedIds.join(','), op: first.rawOp || '', start: first.startTime || '' },
      })
    },
    showOnMap(row) {
      if (row.virtual) {
        this.$router.push({ path: '/map', query: { task: row.id } })
      } else {
        this.$router.push({ path: '/map', query: { work: row.id } })
      }
    },
    async saveMerge() {
      const selectedRows = this.rows.filter(r => r.virtual && this.selectedIds.includes(r.id))
      if (!selectedRows.length) return
      const sources = this.selectedIds.map(id => {
        const sep = id.indexOf('-')
        return { session_id: id.slice(0, sep), task_group_id: id.slice(sep + 1) }
      })
      const sorted = selectedRows.slice().sort((a, b) => a.time - b.time)
      const distSum = selectedRows.reduce((acc, r) => acc + (r.distM || 0), 0)
      this.saving = true
      try {
        await feedApiClient.createWorkRecord({
          kind: 'merge',
          sources,
          params: {},
          name: this.mergeDialog.name,
          memo: this.mergeDialog.memo,
          operation_type: sorted[0].rawOp || null,
          started_at: sorted[0].startTime || null,
          cache: { distance_m: Math.round(distSum) || null },
        })
        Toast.i18nSuccess('feed.workSaved')
        this.mergeDialog.show = false
        this.selectedIds = []
        this.loadWorks()
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.loadError')
      } finally {
        this.saving = false
      }
    },
    manualDurationMin(m) {
      if (m.duration_min) return m.duration_min
      if (m.started_at && m.ended_at) {
        const ms = new Date(m.ended_at) - new Date(m.started_at)
        if (ms > 0) return Math.round(ms / 60000)
      }
      return null
    },
    shareManual(m) {
      const durationMin = this.manualDurationMin(m) || undefined
      this.shareMeta = {
        operation_type: this.getLocalizedOperationType(m.operation_type),
        started_at: m.started_at,
        duration_min: durationMin,
      }
      this.sharePhotoIds = (m.photos || []).map(p => p.id)
      this.shareDialog = true
    },
    async deleteManual() {
      const t = this.manualDeleteTarget
      if (!t) return
      try {
        await feedApiClient.deleteManualRecord(t.id)
        this.manualRecords = this.manualRecords.filter(r => r.id !== t.id)
      } catch (e) {
        console.error(e.response || e)
      } finally {
        this.manualDeleteTarget = null
      }
    },
    async deleteWork() {
      const w = this.deleteTarget
      if (!w) return
      try {
        await feedApiClient.deleteWorkRecord(w.id)
        this.works = this.works.filter(r => r.id !== w.id)
      } catch (e) {
        console.error(e.response || e)
      } finally {
        this.deleteTarget = null
      }
    },
  },
}
</script>

<style scoped>
.clickable-row {
  cursor: pointer;
}
.manual-detail-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.manual-detail-thumb {
  width: calc(50% - 3px);
  max-width: 200px;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 6px;
}
</style>
